<template>
  <div class="bg-white p-4 rounded-lg shadow mb-4">
    <div class="flex flex-wrap items-center justify-between gap-2 p-4">
      <div />
      <div class="flex items-center gap-2">
        <SearchBarComponent
          :data="crewTypes"
          search-key="name"
          class="w-64"
          @filtered="handleSearchFilter"
        />
        <Button
          icon="pi pi-plus"
          label="Add Crew Type"
          class="p-button-sm"
          severity="secondary"
          raised
          @click="openAddModal"
        />
      </div>
    </div>
  </div>
  <div class="bg-white p-4 rounded-lg shadow mb-4">
    <AlphabetFilter
      :data="crewTypes"
      filter-key="name"
      @filtered="handleAlphabetFilter"
    />
  </div>
  <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
    <div
      v-for="(crewType, index) in filteredCrewTypes"
      :key="crewType.id || index" 
      class="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-300 overflow-hidden cursor-pointer"
      @click="navigateToCrewType(crewType.id)"
    >
      <!-- Card Header with Consistent Background Color -->
      <div
        class="h-40 relative flex items-center justify-center"
        :style="{ backgroundColor: getCrewTypeColor(crewType.icon) }"
      >
        <div class="w-24 h-24 rounded-full overflow-hidden border-4 border-white flex items-center justify-center">
          <img
            v-if="getSelectedIconPath(crewType)"
            :src="getSelectedIconPath(crewType)"
            :alt="crewType.name || crewType.role"
            class="w-16 h-16 object-contain opacity-75"
          >
          <i
            v-else
            :class="getRoleIcon(crewType.role || crewType.name)"
            class="text-gray-400 text-4xl"
          />
        </div>
      </div>

      <!-- Crew Type Details -->
      <div class="p-4">
        <h3 class="text-lg font-semibold text-gray-800 mb-2">
          {{ crewType.name || crewType.role }}
        </h3>
        <div class="flex items-center text-sm text-gray-600 mb-1">
          <span>Day Rate: {{ formatCurrency(crewType.day_rate || crewType.rate) }}</span>
        </div>
        <div class="flex items-center text-sm text-gray-600 mb-1">
          <span>Cost: {{ formatCurrency(crewType.cost) }}</span>
        </div>
        <div
          v-if="crewType.description"
          class="flex items-center text-sm text-gray-600 mb-1"
        >
          <i class="pi pi-info-circle mr-2" />
          <span>{{ crewType.description }}</span>
        </div>
      </div>

      <!-- Action Footer -->
      <div class="px-4 py-2 bg-gray-50 border-t border-gray-200 flex justify-between items-center">
        <div />
        <Button
          icon="pi pi-eye"
          class="p-button-rounded p-button-text p-button-sm"
          @click.stop="viewCrewType(crewType.id)"
        />
      </div>
    </div>
  </div>

  <!-- Add Crew Type Modal -->
  <AddCrewTypeModal
    :visible="isAddModalVisible"
    @update:visible="isAddModalVisible = $event"
    @crew-type-created="handleCrewTypeCreated"
  />
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { getCrewTypes } from '@/services/crewTypesService';
import { formatCurrency } from '@/composables/formatCurrency';
import { getCrewTypeIcon, getCrewTypeColor } from '@/utils/crewTypeIcons';
import SearchBarComponent from '@/components/shared/tableSearch/TableSearch.vue';
import AlphabetFilter from '@/components/shared/alphabetFilter/AlphabetFilterComponent.vue';
import AddCrewTypeModal from '@/components/crewing/CrewTypes/AddCrewTypeModal.vue';

const crewTypes = ref([]);
const filteredCrewTypes = ref([]);
const router = useRouter();
const isAddModalVisible = ref(false);

onMounted(async () => {
    try {
        console.log('[CrewTypesComponent] Fetching crew types from API');
        const response = await getCrewTypes();
        const data = response.data || response;
        crewTypes.value = data.map(crewType => ({
            ...crewType,
            color: getCrewTypeColor(crewType.icon)
        }));
        filteredCrewTypes.value = crewTypes.value;
        console.log('[CrewTypesComponent] Crew types loaded:', crewTypes.value);
    } catch (error) {
        console.error('[CrewTypesComponent] Error fetching crew types:', error);
        crewTypes.value = [];
        filteredCrewTypes.value = [];
    }
});

const handleSearchFilter = (filteredData) => {
    filteredCrewTypes.value = filteredData;
};

const handleAlphabetFilter = (filteredData) => {
    filteredCrewTypes.value = filteredData;
};

const openAddModal = () => {
    isAddModalVisible.value = true;
};

const handleCrewTypeCreated = async () => {
    // Reload crew types list
    try {
        const response = await getCrewTypes();
        const data = response.data || response;
        crewTypes.value = data.map(crewType => ({
            ...crewType,
            color: getCrewTypeColor(crewType.icon)
        }));
        filteredCrewTypes.value = crewTypes.value;
    } catch (error) {
        console.error('[CrewTypesComponent] Error reloading crew types:', error);
    }
};

const navigateToCrewType = (id) => {
    try {
        console.log('Navigating to crew type:', id);
        router.push(`/crewing/crew-type/${id}`);
    } catch (error) {
        console.error('Navigation error:', error);
    }
};

const viewCrewType = (id) => {
    navigateToCrewType(id);
};


// Get selected icon path from crew type icon field
const getSelectedIconPath = (crewType) => {
    if (crewType?.icon) {
        return getCrewTypeIcon(crewType.icon);
    }
    return null;
};

// Fallback to PrimeIcons if custom SVG not available
const getRoleIcon = (role) => {
    const roleName = role?.toLowerCase() || '';
    
    // Sound/Audio
    if (roleName.includes('sound') || roleName.includes('audio')) {
        return 'pi pi-volume-up';
    }
    
    // Lighting
    if (roleName.includes('light')) {
        return 'pi pi-lightbulb';
    }
    
    // Rigging
    if (roleName.includes('rigg') || roleName.includes('rig')) {
        return 'pi pi-link';
    }
    
    // Set/Carpentry
    if (roleName.includes('set') || roleName.includes('carpent') || roleName.includes('build')) {
        return 'pi pi-wrench';
    }
    
    // Stage hands
    if (roleName.includes('stage hand') || roleName.includes('stagehand') || roleName.includes('hand')) {
        return 'pi pi-user';
    }
    
    // Video
    if (roleName.includes('video')) return 'pi pi-video';
    
    // Camera
    if (roleName.includes('camera')) return 'pi pi-camera';
    
    // Driver/Transport
    if (roleName.includes('driver') || roleName.includes('transport')) return 'pi pi-truck';
    
    // Stage (general)
    if (roleName.includes('stage')) return 'pi pi-home';
    
    // Default
    return 'pi pi-user';
};
</script>

<style scoped></style>